import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
    faFolder,
    faTachometerAlt,
    faChartArea,
    faUsers,
    faCogs,
    faCreditCard,
    faBoxesStacked,
    type IconName,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { Accordion, Badge, Button } from 'react-bootstrap'

import smallLogoLight from '../../client/logo/logo-small-light.svg'
import smallLogoDark from '../../client/logo/logo-small-dark.svg'
import fullLogoLight from '../../client/logo/logo-full-light.svg'
import fullLogoDark from '../../client/logo/logo-full-dark.svg'
import CreateNewModule from '../modules/client/available-module/CreateNewModule'
import { hasAccess } from '../auth/utils/Auth'
import { ROLES } from '../../routeConfig'
import { toggleSideNav } from '../../features/settings/settings.slice'
import { FEATURES } from '../../config/features'

const SideNav: React.FC = () => {
    const theme = useAppSelector((state) => state.theme.theme)
    const sideNavToggled = useAppSelector(
        (state) => state.settings.sideNav.toggled
    )
    const topNavFixed = useAppSelector((state) => state.settings.topNav.fixed)
    const initData = useAppSelector((state: any) => state.security.initData)
    const { roles: userRoles } = useAppSelector(
        (state: any) => state.security.user
    )

    const dispatch = useAppDispatch()
    const { pathname } = useLocation()

    const [openKey, setOpenKey] = useState<string | null>(null)
    const sidebarRef = useRef<HTMLElement>(null)

    const getActiveSectionKey = (path: string): string | null => {
        if (path.startsWith('/content/')) return '1'
        if (path.startsWith('/client/modules')) return '3'
        if (path.startsWith('/people/')) return '2'
        if (path.startsWith('/analytics/')) return '4'
        if (path.startsWith('/finance/')) return '5'
        return null
    }

    const activeSectionKey = getActiveSectionKey(pathname)

    useEffect(() => {
        const isDesktopExpanded = window.innerWidth >= 768 && !sideNavToggled
        if (isDesktopExpanded) {
            setOpenKey(getActiveSectionKey(pathname))
        } else {
            setOpenKey(null)
        }
    }, [pathname, sideNavToggled])

    // Close flyout on outside click when sidebar is in collapsed / icon-only mode
    useEffect(() => {
        if (openKey === null) return

        const handleOutsideClick = (e: MouseEvent) => {
            if (window.innerWidth >= 768 && !sideNavToggled) return

            if (
                sidebarRef.current &&
                !sidebarRef.current.contains(e.target as Node)
            ) {
                setOpenKey(null)
            }
        }

        document.addEventListener('mousedown', handleOutsideClick)
        return () =>
            document.removeEventListener('mousedown', handleOutsideClick)
    }, [openKey, sideNavToggled])

    // Close flyout on Escape key
    useEffect(() => {
        if (openKey === null) return

        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpenKey(null)
        }

        document.addEventListener('keydown', handleEsc)
        return () => document.removeEventListener('keydown', handleEsc)
    }, [openKey])

    const handleToggle = (key: string) => {
        setOpenKey((prev) => (prev === key ? null : key))
    }

    const NavAccordionToggle = ({
        eventKey,
        children,
    }: {
        eventKey: string
        children: React.ReactNode
    }) => {
        const isOpen = openKey === eventKey
        return (
            <a
                href="#"
                className={`nav-link ${isOpen ? '' : 'collapsed'}`}
                data-toggle="collapse"
                aria-expanded={isOpen}
                onClick={(e) => {
                    e.preventDefault()
                    handleToggle(eventKey)
                }}
            >
                {children}
            </a>
        )
    }

    const collapseInnerClass = `${
        theme === 'dark' ? 'bg-dark' : 'bg-white'
    } py-2 collapse-inner rounded`

    return (
        <Accordion
            as="nav"
            ref={sidebarRef}
            activeKey={openKey ?? undefined}
            className={`navbar-nav bg-gradient-${
                theme === 'dark' ? 'dark' : 'primary'
            } sidebar sidebar-dark ${sideNavToggled ? 'toggled' : ''} ${
                topNavFixed ? 'topNav-fixed' : ''
            }`}
        >
            <Link
                to={'/'}
                className={`sidebar-brand d-flex align-items-center justify-content-center bg-${
                    theme === 'dark' ? 'dark' : 'white'
                }`}
            >
                <div className="sidebar-brand-icon">
                    <img
                        src={theme === 'dark' ? smallLogoDark : smallLogoLight}
                        alt="Small Logo"
                        className="small-logo"
                    />
                    <img
                        src={theme === 'dark' ? fullLogoDark : fullLogoLight}
                        alt="Full Logo"
                        className="full-logo"
                    />
                </div>
            </Link>
            <hr className="sidebar-divider my-0" />
            <li className={`nav-item ${pathname === '/' ? 'active' : ''}`}>
                <Link className="nav-link" to={'/'}>
                    <FontAwesomeIcon icon={faTachometerAlt} className="me-1" />
                    <span>Dashboard</span>
                </Link>
            </li>
            <hr className="sidebar-divider" />
            <div className="sidebar-heading">My Data</div>

            {/* Content */}
            <li className={`nav-item ${activeSectionKey === '1' ? 'active' : ''}`}>
                <NavAccordionToggle eventKey="1">
                    <FontAwesomeIcon icon={faFolder} className="me-1" />
                    <span>Content</span>
                </NavAccordionToggle>
                <Accordion.Collapse eventKey="1">
                    <div className={collapseInnerClass}>
                        <h6 className="collapse-header">
                            Pages &amp; Sections:
                        </h6>
                        <Link
                            className={`collapse-item ${
                                pathname.startsWith('/content/page-builder')
                                    ? 'active'
                                    : ''
                            }`}
                            to={'/content/page-builder'}
                        >
                            Page Builder
                        </Link>
                        <Link
                            className={`collapse-item ${
                                pathname === '/content/pages' ? 'active' : ''
                            }`}
                            to={'/content/pages'}
                        >
                            Pages
                        </Link>
                        <Link
                            className={`collapse-item ${
                                pathname === '/content/page-sections'
                                    ? 'active'
                                    : ''
                            }`}
                            to={'/content/page-sections'}
                        >
                            Page Sections
                        </Link>
                        <Link
                            className={`collapse-item ${
                                pathname === '/content/page-layout'
                                    ? 'active'
                                    : ''
                            }`}
                            to={'/content/page-layout'}
                        >
                            Page Layout
                        </Link>
                    </div>
                </Accordion.Collapse>
            </li>

            {/* Modules */}
            <li className={`nav-item ${activeSectionKey === '3' ? 'active' : ''}`}>
                <NavAccordionToggle eventKey="3">
                    <FontAwesomeIcon icon={faBoxesStacked} className="me-1" />
                    <span>Modules</span>
                </NavAccordionToggle>
                <Accordion.Collapse eventKey="3">
                    <div className={collapseInnerClass}>
                        {initData?.available_modules?.length > 0 ? (
                            <>
                                <h6 className="collapse-header">Available:</h6>
                                {initData?.available_modules.map(
                                    (availableModule: any) => {
                                        const to =
                                            '/client/modules/' +
                                            availableModule.code
                                        return (
                                            <Link
                                                key={availableModule.code}
                                                className={`collapse-item ${
                                                    pathname === to
                                                        ? 'active'
                                                        : ''
                                                }`}
                                                to={to}
                                            >
                                                <FontAwesomeIcon
                                                    title={
                                                        availableModule.active
                                                            ? 'Active'
                                                            : 'In-Active'
                                                    }
                                                    size="xs"
                                                    icon={
                                                        (availableModule.active
                                                            ? 'check-circle'
                                                            : 'ban') as IconName
                                                    }
                                                    className={`${
                                                        availableModule.active
                                                            ? 'text-success'
                                                            : 'text-danger'
                                                    } me-1`}
                                                />{' '}
                                                {availableModule.name}
                                            </Link>
                                        )
                                    }
                                )}
                            </>
                        ) : (
                            <></>
                        )}
                        <h6 className="collapse-header">New:</h6>
                        <CreateNewModule />
                    </div>
                </Accordion.Collapse>
            </li>
            <hr className="sidebar-divider" />

            <div className="sidebar-heading">Contacts</div>

            {/* People */}
            <li className={`nav-item ${activeSectionKey === '2' ? 'active' : ''}`}>
                <NavAccordionToggle eventKey="2">
                    <FontAwesomeIcon icon={faUsers} className="me-1" />
                    <span>People</span>
                </NavAccordionToggle>
                <Accordion.Collapse eventKey="2">
                    <div className={collapseInnerClass}>
                        <h6 className="collapse-header">Team:</h6>
                        <Link
                            className={`collapse-item ${
                                pathname === '/people/team/members'
                                    ? 'active'
                                    : ''
                            }`}
                            to={'/people/team/members'}
                        >
                            Team Members
                        </Link>
                        <Link
                            className={`collapse-item ${
                                pathname === '/people/team/departments'
                                    ? 'active'
                                    : ''
                            }`}
                            to={'/people/team/departments'}
                        >
                            Departments
                        </Link>
                        <h6 className="collapse-header">Clients:</h6>
                        <Link
                            className={`collapse-item ${
                                pathname.startsWith(
                                    '/people/clients/subscription-requests'
                                )
                                    ? 'active'
                                    : ''
                            }`}
                            to={'/people/clients/subscription-requests'}
                        >
                            Subscriptions
                        </Link>
                        <Link
                            className={`collapse-item ${
                                pathname.startsWith(
                                    '/people/clients/email-campaigns'
                                )
                                    ? 'active'
                                    : ''
                            }`}
                            to={'/people/clients/email-campaigns'}
                        >
                            Email Campaigns
                        </Link>
                    </div>
                </Accordion.Collapse>
            </li>
            <hr className="sidebar-divider" />

            {/* Analytics */}
            {FEATURES.analytics && (
                <>
                    <div className="sidebar-heading">General</div>
                    <li className={`nav-item ${activeSectionKey === '4' ? 'active' : ''}`}>
                        <NavAccordionToggle eventKey="4">
                            <FontAwesomeIcon
                                icon={faChartArea}
                                className="me-1"
                            />
                            <span>Analytics</span>
                        </NavAccordionToggle>
                        <Accordion.Collapse eventKey="4">
                            <div className={collapseInnerClass}>
                                <h6 className="collapse-header">
                                    Google Analytics:
                                </h6>
                                <Link
                                    className={`collapse-item ${
                                        pathname ===
                                        '/analytics/google/categories'
                                            ? 'active'
                                            : ''
                                    }`}
                                    to={'/analytics/google/categories'}
                                >
                                    Categories{' '}
                                    <Badge pill bg="warning">
                                        <small>Soon</small>
                                    </Badge>
                                </Link>
                                <Link
                                    className={`collapse-item ${
                                        pathname ===
                                        '/analytics/google/actions'
                                            ? 'active'
                                            : ''
                                    }`}
                                    to={'/analytics/google/actions'}
                                >
                                    Actions{' '}
                                    <Badge pill bg="warning">
                                        <small>Soon</small>
                                    </Badge>
                                </Link>
                            </div>
                        </Accordion.Collapse>
                    </li>
                </>
            )}

            {/* Finance */}
            <li className={`nav-item ${activeSectionKey === '5' ? 'active' : ''}`}>
                <NavAccordionToggle eventKey="5">
                    <FontAwesomeIcon icon={faCreditCard} className="me-1" />
                    <span>Finance</span>
                </NavAccordionToggle>
                <Accordion.Collapse eventKey="5">
                    <div className={collapseInnerClass}>
                        <Link
                            className={`collapse-item ${
                                pathname === '/finance/payments' ? 'active' : ''
                            }`}
                            to={'/finance/payments'}
                        >
                            Payments
                        </Link>
                    </div>
                </Accordion.Collapse>
            </li>

            {/* Administration */}
            {hasAccess(userRoles, [ROLES.Owner, ROLES.Admin]) && (
                <li className={`nav-item ${pathname.startsWith('/administration') ? 'active' : ''}`}>
                    <Link
                        className={`nav-link ${
                            pathname.startsWith('/administration') ? 'active' : ''
                        }`}
                        to={'/administration'}
                    >
                        <FontAwesomeIcon icon={faCogs} className="me-1" />
                        <span>Administration</span>
                    </Link>
                </li>
            )}
            <hr className="sidebar-divider d-none d-md-block" />
            <div className="text-center d-none d-md-inline">
                <Button
                    className="rounded-circle border-0"
                    id="sidebarToggle"
                    onClick={() => dispatch(toggleSideNav(!sideNavToggled))}
                ></Button>
            </div>
        </Accordion>
    )
}

export default SideNav
